import { IconInfoCircle } from "@tabler/icons-react"
import { type ReactNode } from "react"
import styled from "styled-components"

import Tooltip from "@forento/ui/components/Tooltip"

import { componentBackgroundColor } from "~/utilities/styles"

interface TableProps {
	columns: (string | { label: string; tooltip?: string; flex?: { grow: number; basis: number } })[]
	data: ReactNode[][]
	emptyText?: string
	isCompact?: boolean
	className?: string
}

const defaultFlex = { grow: 1, basis: 0 }

const Table: React.FC<TableProps> = ({ columns, data, emptyText, isCompact = false, className }) => (
	<Container className={className}>
		<TableContent
			role="table"
			$minWidth={columns.reduce(
				(width, column) =>
					width +
					(typeof column === "string" ? 100 : Math.max(column.flex?.basis ?? 100, 100)) +
					(isCompact ? 24 : 48),
				Math.max(columns.length - 1, 0) * (isCompact ? 16 : 32),
			)}
		>
			<Header role="row" $isCompact={isCompact}>
				{columns.map(column => {
					const label = typeof column === "string" ? column : column.label

					return (
						<HeaderCell
							key={label}
							role="columnheader"
							$flex={typeof column !== "string" && column.flex !== undefined ? column.flex : defaultFlex}
							$isCompact={isCompact}
						>
							{typeof column !== "string" && column.tooltip !== undefined ? (
								<StyledTooltip tooltip={column.tooltip} className="flex items-center gap-2">
									{label}
									<IconInfoCircle className="size-3" />
								</StyledTooltip>
							) : (
								label
							)}
						</HeaderCell>
					)
				})}
			</Header>
			{data.map((row, index) => (
				<TableRow key={index} role="row" $isCompact={isCompact}>
					{row.map((column, index) => {
						const columnHeader = columns[index]
						const flex =
							typeof columnHeader !== "string" && columnHeader.flex !== undefined
								? columnHeader.flex
								: defaultFlex
						return (
							<TableCell key={index} role="cell" $flex={flex} $isCompact={isCompact}>
								{column}
							</TableCell>
						)
					})}
				</TableRow>
			))}
		</TableContent>
		{data.length === 0 && emptyText ? <EmptyText>{emptyText}</EmptyText> : null}
	</Container>
)

const Container = styled.div`
	width: 100%;
	background-color: white;
	border: 1px solid var(--color-component-border);
	border-radius: 24px;
	overflow-x: auto;
	display: flex;
	flex-direction: column;
`

const TableContent = styled.div<{ $minWidth: number }>`
	min-width: ${props => props.$minWidth}px;
`

const Header = styled.div<{ $isCompact: boolean }>`
	background: ${componentBackgroundColor};
	display: flex;
	gap: ${props => (props.$isCompact ? 16 : 32)}px;
`

const HeaderCell = styled.div<{ $flex: { grow: number; basis: number }; $isCompact: boolean }>`
	flex: ${props => `${props.$flex.grow} 0 ${props.$flex.basis}px`};
	box-sizing: content-box;
	color: #475569;
	font-weight: 600;
	font-size: 14px;
	padding: 12px ${props => (props.$isCompact ? 12 : 24)}px;
	overflow-wrap: anywhere;
	display: flex;
	align-items: flex-start;
`

const StyledTooltip = styled(Tooltip)`
	color: #475569;
	font-weight: 600;
	font-size: 14px;
`

const TableRow = styled.div<{ $isCompact: boolean }>`
	display: flex;
	gap: ${props => (props.$isCompact ? 16 : 32)}px;
	border-top: 1px solid var(--color-component-border);
	transition: background-color 150ms;

	&:hover {
		background-color: ${componentBackgroundColor};
	}
`

const TableCell = styled.div<{ $flex: { grow: number; basis: number }; $isCompact: boolean }>`
	flex: ${props => `${props.$flex.grow} 0 ${props.$flex.basis}px`};
	box-sizing: content-box;
	font-size: 14px;
	padding: ${props => (props.$isCompact ? 12 : 24)}px;
	overflow-wrap: anywhere;
	display: flex;
	flex-direction: column;
	justify-content: center;
`

const EmptyText = styled.div`
	color: #475569;
	font-weight: 600;
	font-size: 14px;
	padding: 40px 24px;
	text-align: center;
`

export default Table
